import React, { useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import { Button, Spinner } from 'reactstrap';
import assetURLs from 'constants/assetURLs';
import { WHATSAPP_SUPPORT_PHONE } from 'constants/global';
import useDocumentTitle from 'hooks/useDocumentTitle';
import { fetchUser } from 'slices/auth/thunk';
import { AppDispatch, RootState } from 'store';
import '../Authentication/auth.scss';
import './no-agency.scss';

const WHATSAPP_SUPPORT_URL = `https://api.whatsapp.com/send?phone=${WHATSAPP_SUPPORT_PHONE}`;

const getInitials = (name?: string) => {
    if (!name) return '?';
    return name
        .split(' ')
        .filter(Boolean)
        .slice(0, 2)
        .map((part) => part[0]?.toUpperCase())
        .join('');
};

const NoAgency = () => {
    useDocumentTitle('No Brand Access');
    const dispatch = useDispatch<AppDispatch>();
    const user = useSelector((state: RootState) => state.auth.user);
    const [refreshing, setRefreshing] = useState(false);

    const initials = useMemo(() => getInitials(user?.name), [user?.name]);

    const handleRefresh = async () => {
        setRefreshing(true);
        try {
            await dispatch(fetchUser()).unwrap();
        } catch {
            // AuthProtected will keep showing this page until an agency is assigned.
        } finally {
            setRefreshing(false);
        }
    };

    return (
        <div className="d-flex vh-100 auth-page no-agency-page">
            <div className="auth-illustrator-side no-agency-illustrator d-flex flex-column position-relative">
                <div className="grid-bg" />
                <div className="no-agency-illustration__logo">
                    <img src={assetURLs.LOGO_WHITE} alt="Influns" height={28} />
                </div>
                <div className="no-agency-illustration-center">
                    <div className="no-agency-illustration">
                        <h2>Your account is ready, but not linked to a brand yet</h2>
                        <p>
                            Brand workspaces let your team manage campaigns, creators, and collaborations in one place.
                            Once you're added, you'll land right in your dashboard.
                        </p>
                    </div>
                </div>
            </div>

            <div className="auth-side no-agency-side z-1 d-flex flex-column">
                <div className="no-agency-column no-agency-panel">
                    <div className="no-agency-badge d-flex align-items-center justify-content-center">
                        <i className="ri-building-line" aria-hidden="true" />
                    </div>

                    <h2 className="auth-title mb-2">No brand access yet</h2>
                    <p className="auth-helper-text mb-4">
                        Your account is set up, but you haven't been added to a brand workspace yet.
                    </p>

                    {user && (
                        <div className="no-agency-user">
                            {user.profile_photo_url ? (
                                <img
                                    src={user.profile_photo_url}
                                    alt={user.name}
                                    className="no-agency-user__avatar"
                                />
                            ) : (
                                <span className="no-agency-user__avatar">{initials}</span>
                            )}
                            <div className="no-agency-user__meta">
                                <div className="no-agency-user__name">{user.name}</div>
                                <div className="no-agency-user__email">{user.username}</div>
                            </div>
                        </div>
                    )}

                    <div className="no-agency-steps">
                        <div className="no-agency-steps__title">What you can do</div>
                        <div className="no-agency-steps__item">
                            <span className="no-agency-steps__number">1</span>
                            <p className="no-agency-steps__copy mb-0">
                                If you were recently invited, wait a few minutes and refresh your access.
                            </p>
                        </div>
                        <div className="no-agency-steps__item">
                            <span className="no-agency-steps__number">2</span>
                            <p className="no-agency-steps__copy mb-0">
                                Contact support if you believe you should already have access to a brand.
                            </p>
                        </div>
                    </div>

                    <div className="no-agency-actions">
                        <Button
                            color="primary"
                            className="w-100 primary-gradient"
                            onClick={handleRefresh}
                            disabled={refreshing}
                        >
                            {refreshing ? (
                                <>
                                    <Spinner size="sm" className="me-2" />
                                    Checking access...
                                </>
                            ) : (
                                <>
                                    <i className="ri-refresh-line me-2" aria-hidden="true" />
                                    Refresh access
                                </>
                            )}
                        </Button>

                        <a
                            href={WHATSAPP_SUPPORT_URL}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="btn btn-outline-primary w-100 d-inline-flex align-items-center justify-content-center gap-2"
                        >
                            <i className="ri-customer-service-2-line" aria-hidden="true" />
                            Contact support
                        </a>
                    </div>

                    <div className="no-agency-footer">
                        Using a different account?{' '}
                        <Link to="/logout" className="text-decoration-none">
                            Sign out
                        </Link>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default NoAgency;
